import { useEffect, useRef, useState } from "react";
import { subscribeTicks, setAppId } from "@/lib/deriv";
import { useConfig } from "@/hooks/useConfig";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const FEATURED = [
  // v8.5 FOREX-PIVOT — landing showcase now features forex/commodity/crypto.
  { sym: "frxEURUSD", name: "EUR/USD", desc: "Forex Major" },
  { sym: "frxGBPUSD", name: "GBP/USD", desc: "Forex Major" },
  { sym: "frxUSDJPY", name: "USD/JPY", desc: "Forex Major" },
  { sym: "frxXAUUSD", name: "Gold", desc: "Commodity" },
  { sym: "frxXAGUSD", name: "Silver", desc: "Commodity" },
  { sym: "cryBTCUSD", name: "BTC/USD", desc: "Crypto 24/7" },
];

const Sparkline = ({ history, change }) => {
  if (!history?.length || history.length < 2) {
    return <div className="h-10 w-full bg-vts-surface/40 rounded" />;
  }
  const min = Math.min(...history);
  const max = Math.max(...history);
  const range = max - min || 1;
  const w = 100;
  const h = 36;
  const step = w / (history.length - 1);
  const pts = history
    .map((v, i) => `${(i * step).toFixed(2)},${(h - ((v - min) / range) * h).toFixed(2)}`)
    .join(" ");
  const color = change >= 0 ? "#00C853" : "#FF3D00";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`spark-${change >= 0 ? "g" : "r"}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <polygon
        points={`0,${h} ${pts} ${w},${h}`}
        fill={`url(#spark-${change >= 0 ? "g" : "r"})`}
      />
    </svg>
  );
};

const SymbolCard = ({ sym, name, desc, data }) => {
  const last = data?.history?.[data.history.length - 1];
  const first = data?.history?.[0];
  const change = last && first ? last - first : 0;
  const pct = first ? (change / first) * 100 : 0;
  const up = change > 0;
  const down = change < 0;
  return (
    <div
      data-testid={`live-market-card-${sym}`}
      className="vts-card vts-card-hover p-5 group"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-heading font-semibold text-base text-white">{name}</div>
          <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 mt-0.5">
            {desc}
          </div>
        </div>
        <div
          className={`flex items-center gap-1 text-xs font-mono tabular-nums ${
            up ? "text-vts-profit" : down ? "text-vts-loss" : "text-vts-silver"
          }`}
        >
          {up ? (
            <TrendingUp className="h-3 w-3" />
          ) : down ? (
            <TrendingDown className="h-3 w-3" />
          ) : (
            <Minus className="h-3 w-3" />
          )}
          {pct.toFixed(2)}%
        </div>
      </div>
      <div className="font-mono text-2xl text-white tabular-nums font-medium mb-1">
        {last != null ? last.toFixed(last < 10 ? 4 : 2) : "—"}
      </div>
      <Sparkline history={data?.history} change={change} />
    </div>
  );
};

const LiveMarkets = () => {
  const { config } = useConfig();
  const [data, setData] = useState({}); // sym -> {history:[]}
  const unsubsRef = useRef([]);

  useEffect(() => {
    if (!config) return;
    setAppId(config.deriv_app_id);

    FEATURED.forEach(({ sym }) => {
      const unsub = subscribeTicks(sym, (msg) => {
        if (!msg?.tick) return;
        setData((prev) => {
          const cur = prev[sym]?.history || [];
          const next = [...cur, msg.tick.quote];
          if (next.length > 30) next.shift();
          return { ...prev, [sym]: { history: next } };
        });
      });
      unsubsRef.current.push(unsub);
    });

    return () => {
      unsubsRef.current.forEach((u) => u && u());
      unsubsRef.current = [];
    };
  }, [config]);

  return (
    <section
      data-testid="live-markets-section"
      className="relative py-24 lg:py-32 border-t border-vts-line"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-vts-blue font-semibold mb-4">
              04 — Live Markets
            </div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl text-white tracking-tighter">
              Real prices.<br />
              <span className="text-vts-silver">Real time.</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-vts-silver/70 font-mono">
            <span className="h-2 w-2 rounded-full bg-vts-profit animate-pulse" />
            Streaming from ws.derivws.com
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURED.map((f) => (
            <SymbolCard
              key={f.sym}
              sym={f.sym}
              name={f.name}
              desc={f.desc}
              data={data[f.sym]}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveMarkets;
